/**
 * Measure-group range tools: Date Range, Price Range, Date & Price Range.
 *
 * Siblings of the ruler (`measureOverlay.ts`) but with two differences:
 *
 *  - **Persistent.** The ruler is ephemeral — armed, read, torn down. These are
 *    real drawings that save, restore and sync like any other overlay.
 *  - **Axis-constrained.** A Date Range measures TIME, so a vertical drag must
 *    not change it (it always spans the pane height); a Price Range measures
 *    PRICE and always spans the pane width. Only `datePrice` boxes the literal
 *    dragged rectangle.
 *
 * Stats reuse `computeMeasure` / `formatDuration` from the ruler rather than
 * re-deriving them — one definition of "what is a bar count".
 */
import { registerOverlay } from 'klinecharts'
import { isNum, type Coord, type OverlayFigure } from './overlayUtils'
import { DEFAULT_FIGURES, type OverlayTemplateParam, registerOnce } from './registrationUtils'
import { withAlphaOrKeep } from './color'
import { computeMeasure, formatDuration } from './measureOverlay'

export const RANGE_MODES = ['date', 'price', 'datePrice'] as const
export type RangeMode = (typeof RANGE_MODES)[number]

/** Overlay names, keyed by mode. */
export const RANGE_TOOL_NAMES: Record<RangeMode, string> = {
  date: 'dateRange',
  price: 'priceRange',
  datePrice: 'datePriceRange',
}

const BORDER = '#2962ff'
const LABEL_BG = '#2962ff'
/** Fill is derived from the edge colour, so a recolour keeps body and edge in
 *  agreement instead of leaving a blue wash under a red border. */
const FILL_ALPHA = 0.14

/** Style the toolbar can override per drawing. */
export interface RangeStyle {
  color?: string
  size?: number
  style?: 'solid' | 'dashed'
}

/** Pane box, so a mode that ignores one axis spans exactly the visible area.
 *  Defaults are a fallback for callers without bounding info. */
export interface RangeBounds {
  width: number
  height: number
}
const DEFAULT_BOUNDS: RangeBounds = { width: 10_000, height: 10_000 }

/** Edge figures translate the whole range (see `edge` in buildRangeFigures). */
export const FIG_RANGE_EDGE = 'range_edge'

interface RangePoint {
  timestamp: number
  value: number
  dataIndex: number
}

/**
 * Readout text for a range, scoped to what the mode actually measures.
 *
 * Deliberately omits the axis a mode ignores — showing a price delta on a Date
 * Range invites reading meaning into a number the tool didn't measure.
 */
export function rangeReadout(
  mode: RangeMode,
  p0: RangePoint,
  p1: RangePoint,
  pricePrecision: number,
): string {
  const { deltaPrice, deltaPct, elapsedMs, bars } = computeMeasure(p0, p1)
  const price = `${deltaPrice >= 0 ? '+' : '-'}$${Math.abs(deltaPrice).toFixed(pricePrecision)}`
  const pct = `${deltaPct >= 0 ? '+' : '-'}${Math.abs(deltaPct).toFixed(2)}%`
  const time = `${bars} bars, ${formatDuration(elapsedMs)}`
  if (mode === 'date') return time
  if (mode === 'price') return `${price} (${pct})`
  return `${price} (${pct})  ·  ${time}`
}

/**
 * Figures for a range box.
 *
 * `date` pins the vertical extent and `price` pins the horizontal one, so the
 * ignored axis cannot be changed by dragging — that constraint is the whole
 * difference between these three tools.
 */
export function buildRangeFigures(
  mode: RangeMode,
  coordinates: Coord[],
  points: Array<Partial<RangePoint>>,
  pricePrecision: number,
  style: RangeStyle = {},
  bounds: RangeBounds = DEFAULT_BOUNDS,
): OverlayFigure[] {
  if (coordinates.length < 2) return []
  const [a, b] = coordinates
  if (!isNum(a?.x) || !isNum(a?.y) || !isNum(b?.x) || !isNum(b?.y)) return []

  const left = Math.min(a.x, b.x)
  const right = Math.max(a.x, b.x)
  const top = Math.min(a.y, b.y)
  const bottom = Math.max(a.y, b.y)

  // Cull when the range is entirely outside the pane. Previously the ignored
  // axis spanned +/-100_000px, so a scrolled-away range still had geometry
  // crossing the viewport and left artefacts pinned to the chart edge — worst
  // with a dashed edge, where a 200_000px path is ~50k dash segments.
  const offScreenX = mode !== 'price' && (right < 0 || left > bounds.width)
  const offScreenY = mode !== 'date' && (bottom < 0 || top > bounds.height)
  if (offScreenX || offScreenY) return []

  // Degenerate: the measured axis has collapsed to nothing.
  //
  // klinecharts defaults a point it cannot resolve to `{ x: 0, y: 0 }`
  // (dist/index.esm.js:8783), so a range scrolled far off-screen does NOT go
  // negative — both anchors land on 0 and the off-pane check above never
  // fires. The body rect then paints its border as a solid vertical line
  // pinned to the chart's left edge.
  //
  // A range measuring zero conveys nothing anyway, so culling it is right on
  // its own terms, not just as a workaround.
  const collapsedX = mode !== 'price' && right - left < 1
  const collapsedY = mode !== 'date' && bottom - top < 1
  if (collapsedX || collapsedY) return []

  // A mode that ignores an axis spans exactly the pane on that axis.
  const x = mode === 'price' ? 0 : left
  const width = mode === 'price' ? bounds.width : right - left
  const y = mode === 'date' ? 0 : top
  const height = mode === 'date' ? bounds.height : bottom - top

  // BODY IS INERT. Dragging the middle of a measurement should do nothing —
  // the range is defined by its edges, and letting the fill translate the whole
  // overlay made it far too easy to nudge a measurement out of place while
  // trying to read it.
  const stroke = style.color ?? BORDER
  const strokeSize = style.size ?? 1
  // DASHED by default. These are measurements laid over price, not annotations
  // of it — a dashed edge reads as a ruler rather than a drawn level.
  const dashed = style.style !== 'solid'

  const figures: OverlayFigure[] = [
    {
      key: 'range_body',
      type: 'rect',
      ignoreEvent: true,
      attrs: { x, y, width, height },
      // FILL ONLY. `stroke_fill` strokes all four sides, which on a Date Range
      // painted meaningless top/bottom edges, and everywhere drew a SOLID
      // border underneath the dashed edge lines — so both showed at once.
      // Edges are emitted separately below, per mode.
      styles: {
        style: 'fill',
        color: withAlphaOrKeep(stroke, FILL_ALPHA),
      },
    },
  ]

  // Edges MOVE the whole range; the grab DOTS resize it.
  //
  // Deliberately NOT anchor-keyed. An `anchor_<n>` key routes the press to that
  // point's move (resize) via the klinecharts patch; a plain key falls through
  // to `eventPressedOtherMove`, which translates every point together. That
  // split is the whole interaction model here: grab the line to slide the
  // measurement along, grab a dot to stretch it.
  const edge = (from: Coord, to: Coord): OverlayFigure => ({
    key: FIG_RANGE_EDGE,
    type: 'line',
    ignoreEvent: false,
    attrs: { coordinates: [from, to] },
    styles: {
      color: stroke,
      size: strokeSize,
      ...(dashed ? { style: 'dashed', dashedValue: [4, 4] } : {}),
    },
  })

  const vertical = (px: number) => edge({ x: px, y }, { x: px, y: y + height })
  const horizontal = (py: number) => edge({ x, y: py }, { x: x + width, y: py })

  if (mode !== 'price') {
    figures.push(vertical(left))
    figures.push(vertical(right))
  }
  if (mode !== 'date') {
    figures.push(horizontal(top))
    figures.push(horizontal(bottom))
  }

  const p0 = points[0]
  const p1 = points[1]
  if (
    isNum(p0?.value) &&
    isNum(p1?.value) &&
    isNum(p0?.timestamp) &&
    isNum(p1?.timestamp) &&
    isNum(p0?.dataIndex) &&
    isNum(p1?.dataIndex)
  ) {
    figures.push({
      key: 'range_label',
      type: 'text',
      ignoreEvent: true,
      attrs: {
        x: (left + right) / 2,
        y: top - 6,
        text: rangeReadout(mode, p0 as RangePoint, p1 as RangePoint, pricePrecision),
        align: 'center',
        baseline: 'bottom',
      },
      styles: {
        style: 'fill',
        color: '#ffffff',
        size: 11,
        backgroundColor: style.color ?? LABEL_BG,
        paddingLeft: 5,
        paddingRight: 5,
        paddingTop: 3,
        paddingBottom: 3,
        borderRadius: 3,
      },
    })
  }

  return figures
}

/** Register the three range tools. Idempotent. */
export const registerRangeOverlays = registerOnce(() => {
  for (const mode of RANGE_MODES) {
    registerOverlay({
      name: RANGE_TOOL_NAMES[mode],
      totalStep: 3, // two clicks
      ...DEFAULT_FIGURES,
      createPointFigures: ({ coordinates, overlay, precision, bounding }) =>
        buildRangeFigures(
          mode,
          coordinates as Coord[],
          (overlay?.points ?? []) as Array<Partial<RangePoint>>,
          precision?.price ?? 2,
          // Toolbar edits land on `styles.line` via overrideOverlayStyle; the
          // template has to READ them or the controls silently do nothing.
          ((overlay?.styles as { line?: RangeStyle })?.line ?? {}) as RangeStyle,
          bounding as RangeBounds,
        ),
    } as OverlayTemplateParam)
  }
})
